import { useEffect, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { ChallengeService } from '@duck/api-definitions'
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle } from '@duck/ui'
import { HelpCircle, Send } from 'lucide-react'
import { RelativeDateTime } from '@components/datetime'
import { LastWorkSave } from './LastWorkSave'
import { WorkspaceTourDialog } from './WorkspaceTourDialog'
import { useTar } from '@/modules'
import { useUserSettings } from '@/hooks/useUserSettings'
import { useSubmitToReview } from '@/hooks/useSubmitToReview'

interface InformationBarProps {
  challenge: ChallengeService.Challenge
  task: ChallengeService.Task
  workTime: number
  /** When true, show submit button instead of help (used on mobile) */
  showSubmitButton?: boolean
}

export const WorkspaceHeaderPane = ({ challenge, task, workTime, showSubmitButton = false }: InformationBarProps) => {
  const { t } = useTar()
  const navigate = useNavigate()
  const { getSetting, setSetting } = useUserSettings()
  const { handleSubmit } = useSubmitToReview()
  const [isHelpDialogOpen, setIsHelpDialogOpen] = useState(false)
  const [isFiveMinuteDialogOpen, setIsFiveMinuteDialogOpen] = useState(false)
  const [isTimeUpDialogOpen, setIsTimeUpDialogOpen] = useState(false)
  const openedAutomaticallyRef = useRef(false)

  useEffect(() => {
    if (getSetting('dismissedRodeoWorkspaceVideo') !== true) {
      setIsHelpDialogOpen(true)
      openedAutomaticallyRef.current = true
    }
  }, [getSetting])

  useEffect(() => {
    if (!challenge.submittableUntil) {
      return
    }

    const deadlineMs = new Date(challenge.submittableUntil).getTime()
    if (Number.isNaN(deadlineMs)) {
      return
    }

    const triggerAt = deadlineMs - 5 * 60 * 1000
    const now = Date.now()
    const delay = triggerAt - now

    if (delay <= 0) {
      setIsFiveMinuteDialogOpen(true)
      return
    }

    const timeoutId = window.setTimeout(() => {
      setIsFiveMinuteDialogOpen(true)
    }, delay)

    return () => window.clearTimeout(timeoutId)
  }, [challenge.submittableUntil, setIsFiveMinuteDialogOpen])

  useEffect(() => {
    if (!challenge.submittableUntil) {
      return
    }

    const deadlineMs = new Date(challenge.submittableUntil).getTime()
    if (Number.isNaN(deadlineMs)) {
      return
    }

    const now = Date.now()
    const delay = deadlineMs - now

    if (delay <= 0) {
      setIsTimeUpDialogOpen(true)
      return
    }

    const timeoutId = window.setTimeout(() => {
      setIsTimeUpDialogOpen(true)
    }, delay)

    return () => window.clearTimeout(timeoutId)
  }, [challenge.submittableUntil])

  const handleTimeUpDialogClose = (open: boolean) => {
    if (!open) {
      navigate({ to: '/' })
    }
    setIsTimeUpDialogOpen(open)
  }

  const handleOpenChange = (open: boolean) => {
    if (!open && openedAutomaticallyRef.current) {
      setSetting('dismissedRodeoWorkspaceVideo', true)
      openedAutomaticallyRef.current = false
    }
    setIsHelpDialogOpen(open)
  }

  const openFromHelpButton = () => {
    openedAutomaticallyRef.current = false
    setIsHelpDialogOpen(true)
  }

  const formatWorkTime = (workTimeMinutes: number) => {
    const hours = Math.floor(workTimeMinutes / 60)
    const minutes = workTimeMinutes % 60
    if (hours > 0) {
      return `${hours}h ${minutes}m`
    }
    return `${minutes}m`
  }

  return (
    <div className="flex w-full pt-2 items-center justify-center h-10">
      <div className="flex flex-1 items-center justify-between md:px-4">
        <div className="flex items-center" data-tour-id="task-name">
          <span className="font-semibold text-foreground">{task.name}</span>
        </div>

        <div className="flex items-center gap-4 flex-col md:flex-row">
          <LastWorkSave />
          {!challenge.submittableUntil && (
            <>
              <span className="hidden md:block text-foreground">•</span>
              <div className="items-center gap-2 hidden xl:flex">
                <span className="text-sm font-medium text-foreground">{t('common.started')}:</span>
                <RelativeDateTime isoDateString={challenge.startTime} className="text-sm text-foreground" />
              </div>
            </>
          )}
          {challenge.submittableUntil && (
            <>
              <span className="hidden xl:block text-foreground">•</span>
              <div className="items-center gap-2 hidden xl:flex">
                <span className="text-sm font-medium text-foreground">
                  {t('common.remaining')}:
                </span>
                <RelativeDateTime isoDateString={challenge.submittableUntil} className="text-sm text-foreground" onlyDuration />
              </div>
            </>
          )}
          {!challenge.submittableUntil && (
            <>
              <div className="items-center gap-2 hidden md:flex">
                <span className="text-sm font-medium text-foreground">{t('common.workTime')}:</span>
                <span className="text-sm text-foreground">{formatWorkTime(workTime)}</span>
              </div>
            </>
          )}
          {showSubmitButton ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 shrink-0"
              onClick={handleSubmit}
              aria-label={t('common.submit')}
              data-tour-id="submit-solution"
            >
              <Send className="size-5" />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 shrink-0"
              onClick={openFromHelpButton}
              aria-label={t('common.help')}
            >
              <HelpCircle className="size-5" />
            </Button>
          )}
        </div>
      </div>
      <WorkspaceTourDialog open={isHelpDialogOpen} onOpenChange={handleOpenChange} />
      <Dialog open={isFiveMinuteDialogOpen} onOpenChange={setIsFiveMinuteDialogOpen}>
        <DialogContent noMinHeight noCloseButton width="xs">
          <DialogHeader>
            <DialogTitle className="text-center">
              {t('common.youHaveXMinutesToSubmitYourSolution', {
                minutes: challenge.submittableUntil
                  ? Math.max(
                    0,
                    Math.ceil(
                      (new Date(challenge.submittableUntil).getTime() - Date.now()) / (60 * 1000)
                    )
                  )
                  : 5,
              })}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 flex justify-center">
            <Button onClick={() => setIsFiveMinuteDialogOpen(false)}>
              {t('common.ok')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={isTimeUpDialogOpen} onOpenChange={handleTimeUpDialogClose}>
        <DialogContent noMinHeight noCloseButton>
          <DialogHeader>
            <DialogTitle>{t('common.timeIsUpSubmissionIsNowClosed')}</DialogTitle>
          </DialogHeader>
          <div className="mt-4 flex justify-center">
            <Button onClick={() => handleTimeUpDialogClose(false)}>
              {t('common.goHome')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}